import React, { useCallback, useEffect, useState } from 'react';
import { InputAdornment, TextField, IconButton } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { IMaskMixin } from 'react-imask';
import { useDispatch } from 'react-redux';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { TabPanel } from '../../../components/other/Tab/TabPanel';
import { ButtonWithLoader } from '../../../components/ui/Buttons';
import { useFormikCheckInSystem } from '../hooks/useFormikCheckInSystem';
import { useAxiosCheckInSystem } from '../hooks/useAxiosCheckInSystem';
import { TAuthTabs } from '.';
import TermDialog from '../../../components/other/Dialog/TermDialog';
import { useAxiosEnterPassword } from '../hooks/useAxiosEnterPassword';
import { useFormikEnterPassword } from '../hooks/useFormikEnterPassword';
import { actions as authActions } from '../../../store/ducks/auth.duck';

export const IMaskPhoneInput = IMaskMixin(({ ...props }) => {
  return <TextField {...(props as any)} />;
});

interface IProps {
  changeAuthTab: (newTab: TAuthTabs, props?: any) => void;
  initEmail?: string;
  initTab?: number;
  hideGuestBtn?: boolean;
  isDialog?: boolean;
}

export const CheckInSystem: React.FC<IProps> = ({
  changeAuthTab,
  initEmail = '',
  initTab = 0,
  hideGuestBtn,
  isDialog,
}) => {
  const intl = useIntl();
  const [tab, setTab] = useState(initTab);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(show => !show);
  const [dialogContentType, setDialogContentType] = useState('');
  const dispatch = useDispatch();
  const [submitedValues, setSubmitedValues] = useState(null);

  const { makeCheckInSystemRequest, loading, isFound, requestedType } =
    useAxiosCheckInSystem();

  const { handleSubmit, values, handleBlur, handleChange, touched, errors } =
    useFormikCheckInSystem(initEmail, makeCheckInSystemRequest, tab);

  const { makeRequest: makeRequestPass, loading: loadingPass, res } = useAxiosEnterPassword();
  const formik = useFormikEnterPassword(values.email, makeRequestPass);

  useEffect(() => {
    if (res) {
      dispatch(authActions.loginSuccess(res));
    }
  }, [res]);
  const handleDialogOpen = useCallback(
    (contentType: string) => {
      setDialogContentType(contentType);
      setOpenDialog(true);
    },
    [setDialogContentType, setOpenDialog]
  );

  const openRegistration = useCallback(() => {
    const props = {
      initTab: tab,
      initEmail: values.email,
    };
    changeAuthTab('Registration', props);
  }, [tab, values.email]);

  useEffect(() => {
    if (localStorage.getItem('authTab') === '1') {
      openRegistration();
    }
    return () => {
      localStorage.removeItem('authTab');
    };
  }, []);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    try {
      if (values.email && formik.values.password) {
        await handleSubmit(e);
        setSubmitedValues(e);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // check if user found in system
  useEffect(() => {
    if (isFound === null) return;
    if (isFound) {
      if (requestedType === 'email') {
        if (submitedValues) {
          formik.handleSubmit(submitedValues);
        }
      }
    } else {
      openRegistration();
    }
  }, [isFound, requestedType, changeAuthTab, values.email]);

  const disabledSubmitBtn = !values.email || !formik.values.password || loading || loadingPass;
  return (
    <>
      <div className='kt-login__body' style={{ position: 'relative' }}>
        <div className='kt-login__form'>
          {!isDialog ? <div style={{ height: 30 }} /> : null}
          <div className='kt-login__title'>
            <h3>{intl.formatMessage({ id: 'AUTH.WELCOME.TITLE' })}</h3>
          </div>

          <form noValidate autoComplete='off' className='kt-form' onSubmit={onSubmit}>
            <TabPanel value={tab} p={0} index={0}>
              <div className='form-group'>
                <TextField
                  type='email'
                  label={intl.formatMessage({ id: 'AUTH.INPUT.EMAIL' })}
                  placeholder={intl.formatMessage({ id: 'PROFILE.VALIDATION.REQUIRED_EMAIL' })}
                  margin='normal'
                  className='kt-width-full'
                  name='email'
                  variant='outlined'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  helperText={touched.email && errors.email}
                  error={Boolean(touched.email && errors.email)}
                />
              </div>
              <div className='form-group'>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  label={intl.formatMessage({ id: 'AUTH.INPUT.PASSWORD' })}
                  placeholder={intl.formatMessage({ id: 'AUTH.INPUT.PASSWORD_REQUIRED' })}
                  margin='normal'
                  className='kt-width-full'
                  name='password'
                  variant='outlined'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  helperText={formik.touched.password && formik.errors.password}
                  error={Boolean(formik.touched.password && formik.errors.password)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </TabPanel>

            {/* buttons */}
            <div>
              <div className='kt-login__actions' style={{ margin: '15px 0' }}>
                <ButtonWithLoader
                  style={{
                    width: '100%',
                    backgroundColor: disabledSubmitBtn ? '#7c97c080' : '#205373',
                    color: '#fff',
                  }}
                  // onPress={onSubmit}
                  disabled={disabledSubmitBtn}
                  loading={loading}
                >
                  {intl.formatMessage({ id: 'AUTH.MAIN.BUTTON.SIGN_IN' })}
                </ButtonWithLoader>
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '20px 0 15px ',
                }}
              >
                <Link
                  style={{ color: '#006997' }}
                  to='#'
                  onClick={() => changeAuthTab('ForgotPassword')}
                  className='kt-link kt-login__link-forgot'
                >
                  <FormattedMessage id='AUTH.GENERAL.FORGOT_BUTTON' />
                </Link>
              </div>
              {!hideGuestBtn && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    margin: '15px 0',
                  }}
                >
                  <Link
                    style={{ color: '#006997', fontSize: '1.75rem' }}
                    to='/'
                    className='kt-link kt-login__link-forgot'
                  >
                    {intl.formatMessage({ id: 'CONTINUE_AS_GUEST' })}
                  </Link>
                  {/* <Button onClick={() => navigate('/products/catalog')}>
                  </Button> */}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ height: 1, backgroundColor: '#a3a7b833', width: '49%' }} />
                <span
                  style={{
                    color: '#181C32',
                    backgroundColor: '#fff',
                    // fontSize: 16,
                    fontWeight: 'normal',
                    padding: '0 5px',
                  }}
                >
                  {intl.formatMessage({ id: 'IF' })}
                </span>
                <div style={{ height: 1, backgroundColor: '#a3a7b833', width: '49%' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 15 }}>
                <p style={{ color: '#181C32' }}>
                  {`${intl.formatMessage({ id: 'ARE.YOU.NEW.USER' })} `}
                  <Link
                    style={{ color: '#006997' }}
                    to='#'
                    onClick={openRegistration}
                    className='kt-link kt-login__link-forgot'
                  >
                    {intl.formatMessage({ id: 'AUTH.GENERAL.SIGNUP_BUTTON' })}
                  </Link>
                </p>
              </div>
            </div>
          </form>

          <div className='kt-login__menu' style={{ textAlign: 'center' }}>
            <p style={{ color: '#0d0a05' }}>
              Нажимая на кнопку Войти, Вы соглашаетесь с{' '}
              <a
                className='kt-link'
                onClick={() => handleDialogOpen('privacy_policy')}
                style={{ marginRight: 0, whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id='AUTH.LOGIN.PRIVACY_POLICY' />
              </a>{' '}
              и на обработку ваших{' '}
              <a
                className='kt-link'
                onClick={() => handleDialogOpen('user_agreement')}
                style={{ marginRight: 0, whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id='AUTH.LOGIN.LEGAL_TERMS' />
              </a>
            </p>
          </div>
        </div>
        <TermDialog
          isOpen={openDialog}
          handleClose={() => setOpenDialog(false)}
          contentType={dialogContentType}
        />
      </div>
    </>
  );
};
